import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { StepViewer } from 'components/Shared/StepViewer';
import { useStepViewerNavigateOnEnter } from 'components/Shared/StepViewer/hooks/useStepViewerNavigateOnEnter';
import { Form, FormItem, useForm, useWatch } from 'components/UI/Form';
import { Input } from 'components/UI/Input';
import { useOpenAccountAuth } from 'hooks/account/useOpenAccountAuth';
import { useHistory } from 'hooks/generic/useHistory';
import { useFireEvent } from 'hooks/useFireEvent';
import { useIsLoggedIn } from 'hooks/user/useIsLoggedIn';
import { useToken } from 'hooks/user/useToken';
import * as Styled from './styles';
import { useReturnFormFields } from '../../hooks/useReturnFormFields';
import {
  AccountLayout,
  useAccountValues
} from '../../providers/AccountProvider';
import { CheckEmailModal } from '../components/CheckEmailModal';
import { InfoBlockLoggedIn } from '../components/InfoBlockLoggedIn';
import { KYCDrawer } from '../components/KYCDrawer';
import { setUserData } from 'appRedux/actions';
import { useAppDispatch } from 'appRedux/hooks';
import { loginUser } from 'appRedux/restApi';
import { applicantStatus } from 'appRedux/restApi/SumSub';
import { setSession } from 'services/ApiClient';
import { getPath } from 'utils';
import { AccountFormFields } from 'constants/account/account-form-fields';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import {
  ESumsubApplicantStatus,
  ESumsubKeys
} from 'constants/account/sumsub-keys';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import { RestAxiosError } from 'interfaces';

export const Login = () => {
  const loginId = useId();
  const { t } = useTranslation();
  const [form] = useForm();
  const { history, goBack } = useHistory();
  const { isLoggedIn, isLoginPending } = useIsLoggedIn();
  const { configs } = useAccountValues();

  const [showKYC, setShowKYC] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] =
    useState(false);
  const [token, setToken] = useState('');
  const [currentApplicantStatus, setCurrentApplicantStatus] = useState('');
  const email = useWatch('email', form);

  const { refetch, error, isFetching, data, remove } = useQuery<
    Awaited<ReturnType<typeof loginUser>>,
    RestAxiosError
  >({
    queryKey: [EAccountRouteKeys.ACCOUNT_LOGIN],
    queryFn: () => loginUser(form.getFieldsValue(true)),
    enabled: false,
    retry: false
  });

  const {
    data: applicantData,
    refetch: applicantRefetch,
    error: applicantError
  } = useQuery({
    queryKey: [ESumsubKeys.GET_APPLICATION_STATUS, token],
    queryFn: () => applicantStatus(token as string),
    retry: false,
    enabled: false
  });

  const onSubmitLogin = () => {
    form
      .validateFields()
      .then(() => void refetch())
      .catch(() => {});
  };

  const { handleKeyUp } = useStepViewerNavigateOnEnter({
    eventId: loginId,
    callback: onSubmitLogin
  });
  const { openAccountRegister, openAccountForgotPassword } =
    useOpenAccountAuth();
  const dispatch = useAppDispatch();
  const { fireEvent } = useFireEvent();
  const { setUserToken } = useToken();

  const formFields = useReturnFormFields(
    form,
    [AccountFormFields.Email, AccountFormFields.Password],
    true
  );

  const closePage = () => {
    setShowKYC(false);
    setUserToken('');
    history.push('/');
    remove();
  };

  useEffect(() => {
    fireEvent({
      key: isFetching
        ? StepViewerEvents.SetNextLoading
        : StepViewerEvents.UnsetNextLoading,
      id: loginId
    });
  }, [loginId, isFetching]);

  const onSuccess = () => {
    if (data) {
      const { data: userData, token } = data.data;
      dispatch(setUserData(userData));
      setSession(token);
      if (configs?.accountLayout === AccountLayout.ONPAGE) {
        window.location.href = getPath('/');
      } else {
        goBack();
      }
    }
  };

  useEffect(() => {
    if (applicantData) {
      setCurrentApplicantStatus(applicantData?.data?.review_status);
      if (
        applicantData?.data?.review_status ===
          ESumsubApplicantStatus.COMPLETED &&
        applicantData?.data?.review_result?.review_answer === 'GREEN'
      ) {
        setShowKYC(false);
        onSuccess();
      } else {
        setUserToken(token);
        setShowKYC(true);
      }
    }
  }, [applicantData]);

  useEffect(() => {
    if (applicantError) {
      setUserToken(token);
      setShowKYC(true);
    }
  }, [applicantError]);

  useEffect(() => {
    if (error) {
      // check if user is not verified
      if (error?.response?.status === 403) {
        setShowEmailVerificationModal(true);
        return;
      }
      message.error(error?.response?.data?.message);
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      const { data: userData, token } = data.data;
      if (userData.is_kyc_verified || userData.is_company_verified) {
        onSuccess();
      } else {
        setToken(token);
      }
    }
  }, [data]);

  useEffect(() => {
    if (token) {
      applicantRefetch();
    }
  }, [token]);

  if ((isLoggedIn && !data) || isLoginPending) {
    return <InfoBlockLoggedIn loading={isLoginPending} />;
  }

  return (
    <>
      <StepViewer
        header={{
          showStepsProgress: false
        }}
        eventId={loginId}
        onFinish={onSubmitLogin}
        steps={[
          {
            title: {
              text: t('account.loginStepTitle')
            },
            subtitle: t('account.loginStepSubtitle'),
            info: {
              text: (
                <Styled.LoginInfoBlock>
                  <span>{t('account.loginStepSignupText')}</span>{' '}
                  <Styled.LoginInfoSignUp onClick={openAccountRegister}>
                    {t('account.loginStepSignup')}
                  </Styled.LoginInfoSignUp>
                </Styled.LoginInfoBlock>
              )
            },
            nextBtnText: t('account.login'),
            render: () => (
              <>
                <Form layout="vertical" form={form}>
                  <input
                    className="fake-input"
                    id={AccountFormFields.Email}
                    name={AccountFormFields.Email}
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px'
                    }}
                  />
                  <input
                    className="fake-input"
                    name={AccountFormFields.Password}
                    type={AccountFormFields.Password}
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      top: '-9999px'
                    }}
                  />
                  {formFields.map(field => (
                    <FormItem
                      key={field.name}
                      label={field.label}
                      name={field.name}
                      rules={field.rules}
                    >
                      {field?.type === 'password' ? (
                        <Input type="password" onKeyUp={handleKeyUp} />
                      ) : (
                        <Input onKeyUp={handleKeyUp} />
                      )}
                    </FormItem>
                  ))}
                </Form>
                <Styled.LoginForgotPassword onClick={openAccountForgotPassword}>
                  {t('account.loginResetPassword')}
                </Styled.LoginForgotPassword>
                <Styled.LoginBodySpace />
              </>
            )
          }
        ]}
      />
      <CheckEmailModal
        checkEmailModalVisible={showEmailVerificationModal}
        email={email}
        resendOnMount
      />
      {showKYC && (
        <KYCDrawer
          closePage={closePage}
          currentApplicantStatus={currentApplicantStatus}
        />
      )}
    </>
  );
};
